// src/components/AddClienteRecepcionForm.jsx
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Modal, Button, Form, ListGroup, Spinner } from "react-bootstrap";
import "animate.css";
import "../css/addclienteform.css";

import { postClienteRecepcion } from "../helpers/rutaClienterecepcion";

/* ---------------- utils ---------------- */

const clean = (v) => String(v ?? "").trim();

const buildNombreCompleto = ({
  nombre,
  segundoNombre,
  apellido1,
  apellido2,
}) =>
  [clean(nombre), clean(segundoNombre), clean(apellido1), clean(apellido2)]
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

const buildDireccionCompleta = ({
  direccion,
  referenciaDireccion,
  municipio,
  provincia,
  pais,
}) =>
  [
    clean(direccion),
    clean(referenciaDireccion),
    clean(municipio),
    clean(provincia),
    clean(pais) || "Cuba",
  ]
    .filter(Boolean)
    .join(" - ")
    .replace(/\s+/g, " ")
    .trim();

const makeInitial = () => ({
  codrecep: "",

  // Nombre completo
  nombre: "",
  segundoNombre: "",
  apellido1: "",
  apellido2: "",

  // Documento Cuba
  carnetIdentidad: "",

  // Contacto
  telefonoPrincipal: "",
  email: "",

  // Dirección Cuba
  direccion: "",
  referenciaDireccion: "",
  provincia: "",
  municipio: "",
  zonaLogistica: "",
  pais: "Cuba",
});

/* ---------------- Provincias Cuba ---------------- */

const CUBA_PROVINCIAS = [
  "Pinar del Río",
  "Artemisa",
  "La Habana",
  "Mayabeque",
  "Matanzas",
  "Cienfuegos",
  "Villa Clara",
  "Sancti Spíritus",
  "Ciego de Ávila",
  "Camagüey",
  "Las Tunas",
  "Holguín",
  "Granma",
  "Santiago de Cuba",
  "Guantánamo",
];

const normalizarTexto = (valor = "") =>
  String(valor || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const obtenerZonaLogisticaPorProvincia = (provincia = "") => {
  const p = normalizarTexto(provincia);

  const mapa = {
    "LA HABANA": "PUERTO DEL MARIEL",
    "PINAR DEL RIO": "PUERTO DEL MARIEL",
    ARTEMISA: "PUERTO DEL MARIEL",
    MAYABEQUE: "PUERTO DEL MARIEL",
    MATANZAS: "PUERTO DEL MARIEL",

    CIENFUEGOS: "ZONA CENTRAL",
    "VILLA CLARA": "ZONA CENTRAL",
    "SANCTI SPIRITUS": "ZONA CENTRAL",
    "CIEGO DE AVILA": "ZONA CENTRAL",
    CAMAGUEY: "ZONA CENTRAL",
    "LAS TUNAS": "ZONA CENTRAL",

    "SANTIAGO DE CUBA": "SANTIAGO DE CUBA",
    GRANMA: "SANTIAGO DE CUBA",
    HOLGUIN: "SANTIAGO DE CUBA",
    GUANTANAMO: "SANTIAGO DE CUBA",
  };

  return mapa[p] || "";
};

export default function AddClienteRecepcionForm({ setShow }) {
  const [formValues, setFormValues] = useState(makeInitial);
  const [showSuccess, setShowSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  /* ---------------- modal provincia ---------------- */

  const [showProvinciaModal, setShowProvinciaModal] = useState(false);
  const [qProvincia, setQProvincia] = useState("");
  const [selIdxProvincia, setSelIdxProvincia] = useState(0);
  const inputProvinciaRef = useRef(null);

  const provinciaOpts = useMemo(() => {
    const q = clean(qProvincia).toLowerCase();

    if (!q) return CUBA_PROVINCIAS;

    return CUBA_PROVINCIAS.filter((provincia) =>
      provincia.toLowerCase().includes(q)
    );
  }, [qProvincia]);

  const openProvinciaModal = useCallback(() => {
    setShowProvinciaModal(true);
    setQProvincia("");
    setSelIdxProvincia(0);

    setTimeout(() => inputProvinciaRef.current?.focus(), 50);
  }, []);

  const closeProvinciaModal = useCallback(() => {
    setShowProvinciaModal(false);
  }, []);

  const seleccionarProvincia = useCallback(
    (provincia) => {
      if (!provincia) return;

      setFormValues((prev) => ({
        ...prev,
        provincia,
        zonaLogistica: obtenerZonaLogisticaPorProvincia(provincia),
      }));

      closeProvinciaModal();
    },
    [closeProvinciaModal]
  );

  const onProvinciaModalKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeProvinciaModal();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();

      setSelIdxProvincia((i) =>
        Math.min(i + 1, Math.max(0, provinciaOpts.length - 1))
      );

      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();

      setSelIdxProvincia((i) => Math.max(i - 1, 0));

      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();

      const pick = provinciaOpts[selIdxProvincia] || provinciaOpts[0];

      if (pick) seleccionarProvincia(pick);
    }
  };

  /* ---------------- form helpers ---------------- */

  const setField = useCallback((name, value) => {
    setFormValues((prev) => {
      const next = {
        ...prev,
        [name]: value,
      };

      if (name === "provincia") {
        next.zonaLogistica = obtenerZonaLogisticaPorProvincia(value);
      }

      return next;
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const nombreCompleto = useMemo(
    () => buildNombreCompleto(formValues),
    [
      formValues.nombre,
      formValues.segundoNombre,
      formValues.apellido1,
      formValues.apellido2,
    ]
  );

  const direccionCompleta = useMemo(
    () => buildDireccionCompleta(formValues),
    [
      formValues.direccion,
      formValues.referenciaDireccion,
      formValues.municipio,
      formValues.provincia,
      formValues.pais,
    ]
  );

  const validate = () => {
    if (!clean(formValues.carnetIdentidad)) {
      return "Ingresá el Carnet de identidad.";
    }

    if (!clean(formValues.nombre)) {
      return "Ingresá el Nombre.";
    }

    if (!clean(formValues.apellido1)) {
      return "Ingresá el Apellido.";
    }

    if (!clean(formValues.telefonoPrincipal)) {
      return "Ingresá el Teléfono principal.";
    }

    if (!clean(formValues.direccion)) {
      return "Ingresá la Dirección.";
    }

    if (!clean(formValues.provincia)) {
      return "Ingresá la Provincia.";
    }

    if (!clean(formValues.municipio)) {
      return "Ingresá el Municipio.";
    }

    const email = clean(formValues.email);

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return "Ingresá un correo electrónico válido.";
    }

    return "";
  };

  const buildPayload = () => {
    const nombre = clean(formValues.nombre);
    const segundoNombre = clean(formValues.segundoNombre);
    const apellido1 = clean(formValues.apellido1);
    const apellido2 = clean(formValues.apellido2);

    const direccion = clean(formValues.direccion);
    const referenciaDireccion = clean(formValues.referenciaDireccion);
    const provincia = clean(formValues.provincia);
    const municipio = clean(formValues.municipio);
    const zonaLogistica =
      obtenerZonaLogisticaPorProvincia(provincia) ||
      clean(formValues.zonaLogistica);
    const pais = clean(formValues.pais) || "Cuba";

    const payload = {
      codrecep: clean(formValues.codrecep),

      nombre,
      segundoNombre,
      apellido1,
      apellido2,
      nombreCompleto: buildNombreCompleto({
        nombre,
        segundoNombre,
        apellido1,
        apellido2,
      }),

      carnetIdentidad: clean(formValues.carnetIdentidad),

      telefonoPrincipal: clean(formValues.telefonoPrincipal),
      email: clean(formValues.email),

      direccion,
      referenciaDireccion,
      provincia,
      municipio,
      zonaLogistica,
      pais,

      direccionCompleta: buildDireccionCompleta({
        direccion,
        referenciaDireccion,
        municipio,
        provincia,
        pais,
      }),

      activo: true,
    };

    if (!clean(payload.codrecep)) {
      delete payload.codrecep;
    }

    return payload;
  };

  const resetForm = () => {
    setFormValues(makeInitial());
    setQProvincia("");
    setSelIdxProvincia(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const msg = validate();
    if (msg) return alert(msg);

    const payload = buildPayload();

    try {
      setSaving(true);

      await postClienteRecepcion(payload);

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        resetForm();

        if (typeof setShow === "function") {
          setShow(false);
        }
      }, 900);
    } catch (err) {
      console.error(
        "❌ Error al crear cliente recepción:",
        err?.response?.status,
        err?.response?.data || err
      );

      const mensaje =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "No se pudo guardar el cliente de recepción. Revisá consola / Network.";

      alert(mensaje);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="container">
        <form onSubmit={handleSubmit} noValidate>
          {/* ---------------- fila 1 ---------------- */}
          <div className="form-row">
            <div className="form-group mt-3 col-sm-2">
              <label>Código</label>
              <input
                className="form-control"
                name="codrecep"
                value={formValues.codrecep}
                onChange={handleChange}
                placeholder="Auto"
                disabled
              />
            </div>

            <div className="form-group mt-3 col-sm-4">
              <label>Carnet de identidad *</label>
              <input
                className="form-control"
                name="carnetIdentidad"
                value={formValues.carnetIdentidad}
                onChange={handleChange}
                placeholder="Carnet de identidad"
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Teléfono Principal *</label>
              <input
                className="form-control"
                name="telefonoPrincipal"
                value={formValues.telefonoPrincipal}
                onChange={handleChange}
                placeholder="Teléfono principal"
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Correo electrónico</label>
              <input
                type="email"
                className="form-control"
                name="email"
                value={formValues.email}
                onChange={handleChange}
                placeholder="correo@email.com"
              />
            </div>
          </div>

          {/* ---------------- fila 2 ---------------- */}
          <div className="form-row">
            <div className="form-group mt-3 col-sm-3">
              <label>Nombre *</label>
              <input
                className="form-control"
                name="nombre"
                value={formValues.nombre}
                onChange={handleChange}
                placeholder="First Name"
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Segundo nombre</label>
              <input
                className="form-control"
                name="segundoNombre"
                value={formValues.segundoNombre}
                onChange={handleChange}
                placeholder="Middle Name"
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Apellido 1 *</label>
              <input
                className="form-control"
                name="apellido1"
                value={formValues.apellido1}
                onChange={handleChange}
                placeholder="Last Name"
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Apellido 2</label>
              <input
                className="form-control"
                name="apellido2"
                value={formValues.apellido2}
                onChange={handleChange}
                placeholder="Segundo apellido"
              />
            </div>
          </div>

          {/* ---------------- nombre completo ---------------- */}
          <div className="form-row">
            <div className="form-group mt-3 col-sm-12">
              <label>Nombre completo</label>
              <input
                className="form-control"
                value={nombreCompleto}
                readOnly
                placeholder="Se arma automáticamente"
              />
              <small className="text-muted">
                Se guarda como <b>nombreCompleto</b> para búsquedas e informes.
              </small>
            </div>
          </div>

          {/* ---------------- dirección ---------------- */}
          <div className="form-row">
            <div className="form-group mt-3 col-sm-7">
              <label>Dirección *</label>
              <input
                className="form-control"
                name="direccion"
                value={formValues.direccion}
                onChange={handleChange}
                placeholder="Calle, número, casa, apto, edificio, reparto/barrio"
              />
              <small className="text-muted">
                Ej: Calle 10 #125, Apto 3, Reparto Vista Alegre.
              </small>
            </div>

            <div className="form-group mt-3 col-sm-5">
              <label>Referencia / Entre calles</label>
              <input
                className="form-control"
                name="referenciaDireccion"
                value={formValues.referenciaDireccion}
                onChange={handleChange}
                placeholder="Entre calles, cerca de, color de casa, referencia"
              />
              <small className="text-muted">
                Muy útil para entregar sin perderse.
              </small>
            </div>
          </div>

          {/* ---------------- provincia / zona / municipio / país ---------------- */}
          <div className="form-row">
            <div className="form-group mt-3 col-sm-3">
              <label>Provincia *</label>

              <div className="input-group">
                <input
                  className="form-control"
                  name="provincia"
                  value={formValues.provincia}
                  onChange={handleChange}
                  placeholder="Provincia"
                />

                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={openProvinciaModal}
                  title="Buscar provincia"
                  style={{
                    width: 44,
                    height: 38,
                    padding: 0,
                    fontSize: "1rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  🔎
                </button>
              </div>

              <small className="text-muted">Podés escribirla o buscarla.</small>
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Zona logística</label>
              <input
                className="form-control bg-light"
                name="zonaLogistica"
                value={formValues.zonaLogistica || ""}
                readOnly
                placeholder="Automática"
              />
              <small className="text-muted">Se calcula según provincia.</small>
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>Municipio *</label>
              <input
                className="form-control"
                name="municipio"
                value={formValues.municipio}
                onChange={handleChange}
                placeholder="Municipio"
              />
            </div>

            <div className="form-group mt-3 col-sm-3">
              <label>País</label>
              <input
                className="form-control"
                name="pais"
                value={formValues.pais}
                onChange={handleChange}
                placeholder="Cuba"
              />
            </div>
          </div>

          {/* ---------------- dirección completa ---------------- */}
          <div className="form-row">
            <div className="form-group mt-3 col-sm-12">
              <label>Dirección completa</label>
              <input
                className="form-control"
                value={direccionCompleta}
                readOnly
                placeholder="Se arma automáticamente"
              />
              <small className="text-muted">
                Esta dirección se guarda para búsquedas, informes y logística.
              </small>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-dark mt-5 mb-3"
            id="button"
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </form>
      </div>

      {/* ---------------- Modal buscar Provincia ---------------- */}
      <Modal
        show={showProvinciaModal}
        onHide={closeProvinciaModal}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Buscar provincia</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Provincia</Form.Label>
            <Form.Control
              ref={inputProvinciaRef}
              value={qProvincia}
              placeholder="Ej: La Habana"
              onChange={(e) => {
                setQProvincia(e.target.value);
                setSelIdxProvincia(0);
              }}
              onKeyDown={onProvinciaModalKeyDown}
            />

            <Form.Text className="text-muted">
              ↑ ↓ para moverte • Enter selecciona • Esc cierra
            </Form.Text>
          </Form.Group>

          <div className="mt-3" style={{ maxHeight: 320, overflowY: "auto" }}>
            {!provinciaOpts.length && (
              <div className="text-muted">Sin resultados.</div>
            )}

            {!!provinciaOpts.length && (
              <ListGroup>
                {provinciaOpts.map((provincia, idx) => (
                  <ListGroup.Item
                    key={provincia}
                    action
                    active={idx === selIdxProvincia}
                    onClick={() => seleccionarProvincia(provincia)}
                  >
                    {provincia}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeProvinciaModal}>
            Cerrar
          </Button>

          <Button
            variant="primary"
            onClick={() =>
              seleccionarProvincia(
                provinciaOpts[selIdxProvincia] || provinciaOpts[0]
              )
            }
            disabled={!provinciaOpts.length}
          >
            Seleccionar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---------------- Modal éxito ---------------- */}
      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🖐</div>
          <h5 className="mt-2">¡Cliente de recepción generado correctamente!</h5>
        </Modal.Body>
      </Modal>

      {/* ---------------- Spinner guardando ---------------- */}
      <Modal show={saving} centered backdrop="static" keyboard={false}>
        <Modal.Body className="text-center">
          <Spinner animation="border" size="sm" />{" "}
          <span className="ms-2">Guardando cliente de recepción...</span>
        </Modal.Body>
      </Modal>
    </>
  );
}